import React, { useEffect } from "react";
import Select from "components/shared/Select";
import pluralize from "pluralize";
import { useAccessTier } from "contexts/AccessTierContext";
import {
  AI_TOOL_NAMES,
  AI_TOOLS_LOADING_DEFAULTS,
  REUSABLES
} from "../../constants";
import useFormText from "../../hooks/useFormText";
import useWindowResizeAndFormCollapse from "../../hooks/useWindowResizeAndFormCollapse";
import useDiscussionQuestionsFormPopulator from "../../hooks/useDiscussionQuestionsFormPopulator";

const InContentDiscussionFormFields = ({
  formik,
  isStreaming,
  loadingPastMessages,
  options,
  locked,
  loading,
  quotaRemaining,
  resetDate,
  onResourceChange,
  storeFieldForReload,
  infiniteAnswers
}) => {
  const { isLessThanSmallBreakpoint, collapsed } =
    useWindowResizeAndFormCollapse();
  const { buttonText, submittingButtonText } = useFormText(
    AI_TOOL_NAMES.DISCUSSION_QUESTIONS,
    locked,
    quotaRemaining,
    collapsed,
    isLessThanSmallBreakpoint,
    resetDate,
    infiniteAnswers,
    true // inContent is true for InContentDiscussionFormFields
  );

  const { isAPlusTeacher } = useAccessTier();

  const selectedTitle = options.find(
    (option) => option.value === formik.values.title
  );

  const {
    topics,
    loadingTopics,
    selectedTopic,
    setSelectedTopic,
    setSelectedSubtopic,
    fetchDropdownContent
  } = useDiscussionQuestionsFormPopulator(formik, selectedTitle);

  useEffect(() => {
    if (!formik.values.topic || !topics.length) return;

    const foundTopic = topics.find((t) => t.value === formik.values.topic);
    if (foundTopic) {
      setSelectedTopic(foundTopic);

      if (formik.values.subtopic && foundTopic.data?.length) {
        const foundSubtopic = foundTopic.data.find(
          (s) => s.value === formik.values.subtopic
        );
        if (foundSubtopic) {
          setSelectedSubtopic(foundSubtopic);
        }
      }
    }
  }, [formik.values.topic, formik.values.subtopic, topics]);

  const handleTitleChange = (e) => {
    formik.setFieldValue(REUSABLES.TITLE, e.value);
    formik.setFieldValue(REUSABLES.TOPIC, undefined);
    formik.setFieldValue(REUSABLES.SUBTOPIC, undefined);
    formik.setFieldError(REUSABLES.TOPIC, undefined);
    formik.setFieldError(REUSABLES.SUBTOPIC, undefined);
    storeFieldForReload(REUSABLES.TITLE, e.value);
    onResourceChange(e);
    fetchDropdownContent(e.guide_path);
  };

  const handleTopicChange = (e) => {
    formik.setFieldValue(REUSABLES.TOPIC, e.value);
    formik.setFieldValue(REUSABLES.SUBTOPIC, e.subtopic ?? undefined);
    formik.setFieldError(REUSABLES.SUBTOPIC, undefined);
    const selectedTopicObj = topics.find((t) => t.value === e.value);
    setSelectedTopic(selectedTopicObj);
    setSelectedSubtopic(null);
    if (selectedTopicObj?.messageText) {
      formik.setFieldValue(
        REUSABLES.MESSAGE_TEXT,
        selectedTopicObj.messageText
      );
    }
    storeFieldForReload(REUSABLES.TOPIC, e.value);
  };

  const handleSubtopicChange = (e) => {
    formik.setFieldValue(REUSABLES.SUBTOPIC, e.value);
    const selectedSubtopicObj = selectedTopic.data.find(
      (t) => t.value === e.value
    );
    setSelectedSubtopic(selectedSubtopicObj);
    if (selectedSubtopicObj?.messageText) {
      formik.setFieldValue(
        REUSABLES.MESSAGE_TEXT,
        selectedSubtopicObj.messageText
      );
    }
    storeFieldForReload(REUSABLES.SUBTOPIC, e.value);
  };

  const subTopicPlaceholder = formik.values.topic
    ? `Select a ${pluralize(
        formik.values.topic.toLowerCase().replace(" summary & analysis", ""),
        1,
        false
      )}`
    : "";

  return (
    <>
      <Select
        errorMessage={formik.errors.title}
        id={REUSABLES.TITLE}
        isDisabled={isStreaming || loading || loadingPastMessages}
        isSearchable
        name={REUSABLES.TITLE}
        menuPlacement="top"
        options={options}
        placeholder="e.g. Hamlet (Shakespeare)"
        onChange={handleTitleChange}
        value={formik.values.title}
      />

      <Select
        errorMessage={
          loadingTopics && formik.errors.topic
            ? "Loading, select one when complete"
            : formik.errors.topic
        }
        id={REUSABLES.TOPIC}
        isDisabled={
          loadingTopics || loading || isStreaming || loadingPastMessages
        }
        isSearchable
        isControlled
        menuPlacement="top"
        isLoading={loadingTopics}
        name={REUSABLES.TOPIC}
        options={loadingTopics ? [AI_TOOLS_LOADING_DEFAULTS] : topics}
        placeholder="Select a topic"
        onChange={handleTopicChange}
        value={
          loadingTopics ? AI_TOOLS_LOADING_DEFAULTS.value : formik.values.topic
        }
      />

      {selectedTopic?.data && formik.values.topic && (
        <Select
          errorMessage={formik.errors.subtopic}
          id={REUSABLES.SUBTOPIC}
          isDisabled={isStreaming || loading || loadingPastMessages}
          isSearchable
          isControlled
          menuPlacement="top"
          name={REUSABLES.SUBTOPIC}
          options={selectedTopic.data}
          placeholder={subTopicPlaceholder}
          onChange={handleSubtopicChange}
          value={formik.values.subtopic}
        />
      )}
      <div className="!tw-m-0 !tw-p-0 !tw-max-w-[100%] ai-form--footer">
        <button
          className="ai-form--submit btn btn-a-plus button-1 btn-md"
          data-testid="ai-form-submit"
          data-amplitude={
            isAPlusTeacher ? "aiContentDqgSubmit" : "aiContentDqgSubmitAttempt"
          }
          disabled={loading}
          type="submit"
        >
          {locked && (
            <i
              className="lc-icon lc-icon-lock-filled lc-icon-sm"
              data-testid="lock-icon"
            />
          )}
          {loading ? submittingButtonText : buttonText}
        </button>
      </div>
    </>
  );
};

export default InContentDiscussionFormFields;
